import React, { useEffect, useState } from 'react';
import useAuth from 'Modules/firebase/use-auth';
import { useParams, useSearchParams } from 'react-router-dom';
import WavingHandOutlined from '@mui/icons-material/WavingHandOutlined';
import Typography from '@mui/material/Typography';
import { styled, Button, Stack } from '@mui/material';
import BackgroundImage from '../../components/general/BackgroundImage';
import { StyledTwoScreensWrapper } from '../../components/modules/AuthPage/Styles';
import { MessageWithTipMui } from '../../components/modules/AuthPage/setup/MessageWithTipMui';
import welcomeImage from '@/operator/public/assets/images/background/welcome.svg';
import { appRoutes } from '@/routes/constants';
import { useSubdomain } from 'hooks/useSubdomain';
import {
    Invitation,
    InvitationStatus,
    useGetInvitationQuery,
} from 'generated/graphql';
import dayjs from 'dayjs';
import Loader from 'Components/Loader';
import { InfoOutlined } from '@mui/icons-material';
import { getOrgPathName } from '@/operator/utils/env/urlStrings';
import AppVersion from 'Components/Common/AppVersion';
import { useUserOrgs } from "@/operator/hooks/useUserOrgs";
import { useOperator } from "app/contexts/OperatorContext";

const StyledPurpleButton = styled(Button)(() => ({
    backgroundColor: '#4c17ad',
    padding: '8px 15px',

    '&:disabled': {
        backgroundColor: '#bf9dff ',
        color: 'white',
    },
    '&:hover': {
        backgroundColor: '#4c17ad',
    },
    minWidth: '8px',
    minHeight: '25px',
    color: '#ffffff',
    textTransform: 'none',

    '@media (width <= 850px)': {
        width: '100%',
        justifyContent: 'center',
    },
}));

interface DescriptionProps {
    invitation: Invitation;
    expired: boolean;
    expiration: dayjs.Dayjs;
}

const Description: React.FC<DescriptionProps> = ({
    invitation,
    expired,
    expiration,
}) => {
    const { id: invitationId, email, status } = invitation;
    const { loginWithRedirect } = useAuth();

    const { subdomain: organization } = useSubdomain();

    if (status === InvitationStatus.Canceled) {
        return (
            <Stack spacing={1}>
                <Typography variant="body1">
                    Looks like your invitation to join{' '}
                    <strong>{organization}</strong> on Operator was canceled and
                    is not longer available.
                </Typography>
                <Typography variant="body1">
                    Please reach out to the inviter or an admin to request a new
                    one.
                </Typography>
            </Stack>
        );
    }

    if (status === InvitationStatus.Accepted) {
        return (
            <Stack spacing={1}>
                <Typography variant="body1">
                    You’ve already accepted the invitation to join{' '}
                    <strong>{organization}</strong> on Operator.
                </Typography>
                <Typography variant="body1">
                    You can sign in to your account to get started.
                </Typography>
                <StyledPurpleButton
                    variant="contained"
                    onClick={() => {
                        window.location.href = getOrgPathName(organization);
                    }}
                    style={{ justifyContent: 'center' }}
                >
                    Sign In
                </StyledPurpleButton>
            </Stack>
        );
    }

    if (expired) {
        return (
            <Stack spacing={1}>
                <Typography variant="body1">
                    Looks like your invitation to join{' '}
                    <strong>{organization}</strong> on Operator expired{' '}
                    {expiration.fromNow()}.
                </Typography>
                <Typography variant="body1">
                    Please reach out to the inviter or an admin to request a new
                    one.
                </Typography>
            </Stack>
        );
    }

    return (
        <>
            <Typography variant="body1">
                We were wondering when you would RSVP. You’ve been invited by{' '}
                <strong>{organization}</strong> to join them on Operator.
            </Typography>
            <Typography variant="body1">
                Just sign in and we can get you started.
            </Typography>
            <StyledPurpleButton
                variant="contained"
                onClick={() =>
                    loginWithRedirect({
                        appState: {
                            targetUrl: appRoutes.AUTH_SETUP_ONBOARDING,
                        },
                        authorizationParams: {
                            invitation: invitationId,
                            email,
                        },
                    })
                }
                style={{ justifyContent: 'center' }}
                disabled={expired}
            >
                Sign In
            </StyledPurpleButton>
        </>
    );
};

const AcceptInvite = (): JSX.Element => {
    const [searchParams] = useSearchParams();
    const { user, logout } = useAuth();
    const { setOrganizationId } = useOperator();
    const { userOrganizations } = useUserOrgs(false);
    const { orgId: orgSlug } = useParams<{ orgId: string }>();

    const invitationId = searchParams.get('invitation') || undefined;

    const [expired, setExpired] = useState(false);
    const [expiration, setExpiration] = useState<dayjs.Dayjs>(null);

    const { data, loading: loadingInvite } = useGetInvitationQuery({
        variables: {
            id: invitationId,
        },
        skip: Boolean(user),
    });

    useEffect(() => {
        if (user) {
            const pickedOrg = userOrganizations?.find((org) => org.slug === orgSlug);
            logout().then(() => {
                setOrganizationId(pickedOrg?.orgId || '');
            });
            return;
        }

        const invitation = data?.invitation;
        if (!invitation?.expiresAt || loadingInvite) return;

        if (invitation.status === InvitationStatus.Pending) {
            const now = dayjs();
            const _expiration = dayjs.unix(invitation.expiresAt);
            setExpiration(_expiration);
            setExpired(_expiration.isBefore(now));
        }
    }, [data, loadingInvite]);

    if (loadingInvite) {
        return <Loader />;
    }

    const getHeadline = () => {
        const status = data?.invitation?.status;
        if (status === InvitationStatus.Canceled) return 'Invitation canceled';

        if (status === InvitationStatus.Accepted) return 'Invitation accepted';

        if (expired) return 'Invitation expired';

        return 'Ohh... hey, you made it!';
    };

    if (!data?.invitation) {
        return (
            <StyledTwoScreensWrapper>
                <MessageWithTipMui
                    Icon={InfoOutlined}
                    headlineVariant="h2"
                    headline={'No invitation found'}
                    description={
                        <Stack spacing={1}>
                            <Typography variant="body1">
                                Invitation not found or not longer available.
                            </Typography>
                            <Typography variant="body1">
                                Please reach out to the inviter or an admin to
                                request a new one.
                            </Typography>
                        </Stack>
                    }
                    footerSlot={<AppVersion />}
                />
                <BackgroundImage src={welcomeImage} />
            </StyledTwoScreensWrapper>
        );
    }

    return (
        <StyledTwoScreensWrapper>
            <MessageWithTipMui
                Icon={
                    expired ||
                    data?.invitation?.status === InvitationStatus.Canceled
                        ? InfoOutlined
                        : WavingHandOutlined
                }
                headlineVariant="h2"
                headline={getHeadline()}
                description={
                    <Description
                        invitation={data?.invitation}
                        {...{ expired, expiration }}
                    />
                }
                footerSlot={<AppVersion />}
            />
            <BackgroundImage src={welcomeImage} />
        </StyledTwoScreensWrapper>
    );
};

export default AcceptInvite;
